/**
 * External dependencies
 */
import React, { useState, useRef, useEffect } from "react";
import { connect } from "react-redux";
import { noop, map } from "lodash";
import Dropdown from "react-dropdown";

/**
 * Internal dependencies
 */
import FormFieldset from "components/forms/form-fieldset";
import FormLabel from "components/forms/form-label";
import { saveDashboardLayout } from "state/dashboard-layouts/actions";
import {
  getEditedId,
  getEditedDashboard,
} from "state/ui/dashboard-form/selectors";
import { requestReports } from "state/reports/actions";
import { ButtonV2 } from "components/button-v2";
import { Text } from "components/text";

/**
 * Style dependencies
 */
import "./styles.scss";

const DASHBOARDS = [
  {
    id: "",
    label: "Empty",
  },
  {
    id: "default",
    label: "Default",
  },
  {
    id: "supplier",
    label: "Supplier",
  },
];

const EditDashboardForm = ({
  data = {},
  dashboardId,
  afterSave = noop,
  onCancel = noop,
  ...props
}) => {
  const [name, setName] = useState(data.name || "");
  const [position, setPosition] = useState(data.position || 0);
  const [dashboardTemplate, setDashboardTemplate] = useState("");

  const [isSaving, setSaving] = useState(false);
  const nameInput = useRef(null);

  useEffect(() => {
    nameInput.current.focus();
  }, []);

  const validPositionInput = React.useMemo(() => {
    // make sure position is a number and integer
    return (
      position !== "" && !isNaN(position) && Number.isInteger(Number(position))
    );
  }, [position]);

  const validNameInput = React.useMemo(() => {
    return name.length > 0;
  }, [name]);

  const validForm = validPositionInput && validNameInput;

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      let data = {
        name,
        position: parseInt(position),
        template: dashboardTemplate ? dashboardTemplate : undefined,
      };

      await props.saveDashboardLayout(dashboardId, data);
      await props.requestReports(); // dashboard api does not return any new reports
      afterSave(dashboardId, { name, data });
    } catch (e) {
      console.log(e);
    }
    setSaving(false);
  };

  const isDisabled = isSaving || !validForm;
  const isNew = !dashboardId;

  return (
    <form onSubmit={handleSave} className="flex p-6 flex-col ">
      {isNew && (
        <FormFieldset>
          <FormLabel htmlFor="name">
            <Text size="xsmall" weight="medium">
              Select existing deck template
            </Text>
          </FormLabel>
          <Dropdown
            value={dashboardTemplate}
            options={map(DASHBOARDS, ({ id, label }) => ({
              value: id,
              label: label,
            }))}
            onChange={({ value }) => setDashboardTemplate(value)}
          />
        </FormFieldset>
      )}
      <FormFieldset>
        <Text variant="headingXxs" className="mb-4 ">
          Deck name
        </Text>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          name="name"
          autoComplete="off"
          ref={nameInput}
        />
      </FormFieldset>
      <FormFieldset>
        <Text variant="headingXxs" className="mb-4 ">
          Position
        </Text>
        <input
          type="number"
          name="position"
          onChange={(e) => setPosition(e.target.value)}
          value={position}
          pattern="[0-9]*"
          inputmode="numeric"
        />
      </FormFieldset>

      <div className="flex gap-3 mt-6 ">
        <ButtonV2
          type="submit"
          disabled={isDisabled}
          variant="primary"
          fullWidth
        >
          {dashboardId ? "Save deck" : "Create deck"}
        </ButtonV2>
        <ButtonV2 onClick={() => onCancel()} fullWidth>
          Cancel
        </ButtonV2>
      </div>
    </form>
  );
};

export default connect(
  (state) => {
    let dashboardId = getEditedId(state);
    let data = getEditedDashboard(state) || {};

    return {
      dashboardId,
      data,
    };
  },
  { saveDashboardLayout, requestReports },
)(EditDashboardForm);
